"use client";

interface FilterPillsProps<T extends string> {
  filters: readonly T[];
  activeFilters: Set<T>;
  onToggle: (filter: T) => void;
  counts?: Partial<Record<T, number>>;
  /** Optional label override — falls back to the filter id when absent. */
  getLabel?: (filter: T) => string;
}

/**
 * Reusable filter pill bar. Used on responsibilities, chores, and meals pages.
 * Matches iOS capsule pill pattern: accent bg when active, gray when inactive.
 */
export function FilterPills<T extends string>({
  filters,
  activeFilters,
  onToggle,
  counts,
  getLabel,
}: FilterPillsProps<T>) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {filters.map((filter) => {
        const isActive = activeFilters.has(filter);
        const count = counts?.[filter];
        return (
          <button
            key={filter}
            onClick={() => onToggle(filter)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
              isActive
                ? "bg-[color:var(--accent-color)] text-white"
                : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300"
            }`}
          >
            {getLabel ? getLabel(filter) : filter}
            {count !== undefined && (
              <span className={`text-xs ${isActive ? "text-white/80" : "text-gray-400"}`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
